import { useState, useEffect, useContext, useRef } from "react"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import cc from "classcat"
import { LocaleContext } from "contexts"
import {
  XCircleIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/20/solid"
import Spin from "images/spin.svg"
import Modal from "components/molecules/Modal"
import { useStaticQuery, graphql } from "gatsby"
import useTranslations from "hooks/useTranslations"
import { getSchoolsPdf } from "utils/forms"
import Altcha from "components/atoms/Altcha"

const BrochureCover = ({ className }) => {
  const { locale } = useContext(LocaleContext)
  const images = useStaticQuery(graphql`
    {
      brochure_cover_fi: file(
        relativePath: { eq: "schools/someturva-school-brochure-2024-fi.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 600)
        }
      }
      brochure_cover_sv: file(
        relativePath: { eq: "schools/someturva-school-brochure-2024-sv.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 600)
        }
      }
      brochure_cover_sv_se: file(
        relativePath: { eq: "schools/someturva-school-brochure-2024-sv-se.jpg" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 90, width: 600)
        }
      }
    }
  `)

  let image

  switch (locale) {
    case "fi-fi":
      image = images.brochure_cover_fi
      break
    case "sv-fi":
      image = images.brochure_cover_sv
      break
    case "sv-se":
      image = images.brochure_cover_sv_se
      break
    default:
      image = images.brochure_cover_fi
  }

  return (
    <GatsbyImage
      image={getImage(image)}
      imgStyle={{
        objectFit: "cover",
        objectPosition: "right center",
      }}
      className={className}
    />
  )
}

const Content = ({ Icon, text, description, color }) => {
  return (
    <div className="flex flex-col items-center space-y-4">
      <Icon className={cc(["w-20 ", color])} aria-hidden="true" />
      <h3 className="m-0 text-center text-2xl font-medium leading-7 text-zinc-800">
        {text}
      </h3>
      <p className="m-0 grow whitespace-pre-line text-base text-stone-800">
        {description}
      </p>
    </div>
  )
}

const Label = ({ children, htmlFor }) => {
  return (
    <label htmlFor={htmlFor} className="mb-2 ml-4 block text-zinc-200">
      {children}
    </label>
  )
}

const Form = () => {
  const {
    formFieldName,
    formFieldEmail,
    formFieldJobTitle,
    schoolsPdfFormJobOptions,
    schoolsPdfFormButton,
    schoolsPdfErrorGeneral,
    schoolsPdfErrorUserExist,
    schoolsPdfErrorContactUs,
    schoolsPdfSuccess,
  } = useTranslations()

  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [job, setJob] = useState(schoolsPdfFormJobOptions?.[0])
  const [submitState, setSubmitState] = useState("ready")
  const [errMessage, setErrMessage] = useState(schoolsPdfErrorGeneral)
  const altchaRef = useRef(null)

  const { locale } = useContext(LocaleContext)

  const [showModal, setShowModal] = useState(false)
  const [modalContent, setModalContent] = useState({
    Icon: XCircleIcon,
    text: "",
    color: "",
  })

  const successfulSubmission = {
    text: schoolsPdfSuccess,
    Icon: CheckCircleIcon,
    color: "text-kkm-green",
  }

  const errorSubmission = {
    text: errMessage,
    description: schoolsPdfErrorContactUs,
    Icon: ExclamationCircleIcon,
    color: "text-red-500",
  }

  useEffect(() => {
    if (!showModal) {
      setTimeout(() => {
        setModalContent("")
      }, 300)
    }
  }, [showModal])

  useEffect(() => {
    if (submitState === "success") {
      setModalContent(successfulSubmission)
    }
    if (submitState === "error") {
      setModalContent(errorSubmission)
    }
  }, [submitState])

  const closeModal = () => {
    setShowModal(false)
  }

  const resetFields = () => {
    setName("")
    setEmail("")
    setJob("")
  }

  const sendForm = async (event) => {
    event.preventDefault()
    let res
    setSubmitState("loading")
    const altcha = altchaRef.current?.value
    try {
      res = await getSchoolsPdf({ name, email, job }, locale, altcha)

      if (res.status === 200) {
        setSubmitState("success")
        setShowModal(true)
        resetFields()
      }
    } catch ({ response }) {
      if (response.data.title === "Invalid Resource") {
        setErrMessage(response.data.detail)
      } else if (response.data.title === "Member Exists") {
        setErrMessage(schoolsPdfErrorUserExist)
      }
      setSubmitState("error")
      setShowModal(true)
      setTimeout(() => {
        setSubmitState("ready")
      }, 3000)
    }
  }

  const handleNameChange = (event) => {
    setName(event.target.value)
  }

  const handleEmailChange = (event) => {
    setEmail(event.target.value)
  }

  const handleJobChange = (event) => {
    setJob(event.target.value)
  }

  return (
    <div>
      <form
        onSubmit={sendForm}
        className="m-0 grid grid-cols-1 gap-y-4 sm:-mt-5"
      >
        <div>
          <Label htmlFor="name">{formFieldName}</Label>
          <input
            type="text"
            name="name"
            id="name"
            autoComplete="name"
            className="block w-full rounded-full border-zinc-300 px-4 py-3 placeholder-zinc-400 shadow-sm focus:border-teal-500 focus:ring-teal-500"
            value={name}
            onChange={handleNameChange}
          />
        </div>
        <div>
          <Label htmlFor="email">{formFieldEmail}</Label>
          <input
            name="email"
            type="email"
            autoComplete="email"
            className="block w-full rounded-full border-zinc-300 px-4 py-3 placeholder-zinc-400 shadow-sm focus:border-teal-500 focus:ring-teal-500"
            value={email}
            onChange={handleEmailChange}
            required
          />
        </div>
        <div>
          <Label htmlFor="job">{formFieldJobTitle}</Label>
          <select
            name="job"
            className="block w-full rounded-full border-zinc-300 px-4 py-3 placeholder-zinc-400 shadow-sm focus:border-teal-500 focus:ring-teal-500"
            placeholder="Job position"
            value={job}
            onChange={handleJobChange}
          >
            {schoolsPdfFormJobOptions?.map((option) => (
              <option>{option}</option>
            ))}
          </select>
        </div>
        <div className="mt-5 text-sm font-bold text-zinc-700 ">
          <Altcha
            ref={altchaRef}
            style={{
              "--altcha-border-radius": "2rem",
              "--altcha-color-border": "rgb(212, 212, 216)",
              "--altcha-color-active": "teal",
              "--altcha-color-border-focus": "#2563eb",
              "--altcha-color-base": "rgba(255, 255, 255, 0.75)",
            }}
          />
        </div>
        <div className="mt-6 max-w-md sm:shrink-0">
          <button
            className={cc([
              "flex w-full items-center justify-center rounded-full border border-transparent px-5 py-3 text-base font-bold leading-6 text-white  transition-colors duration-150 ease-in-out focus:outline-none",
              {
                "bg-red-500 focus:bg-red-400": submitState === "error",
                // hover:bg-teal-400 belongs to the ready state, not the base:
                // it was written `hover::` so it never ran, and applying it at
                // the base level would turn the red error button teal on hover.
                "bg-teal-500 hover:bg-teal-400 focus:bg-teal-400":
                  submitState === "ready" || submitState === "loading",
                "bg-kkm-green focus:bg-kkm-green": submitState === "success",
              },
            ])}
            type="submit"
          >
            {submitState === "loading" && (
              <Spin className="absolute h-5 w-5 animate-spin text-white transition-opacity" />
            )}

            <CheckCircleIcon
              className={cc([
                "absolute h-6 w-6 text-white transition-opacity",
                {
                  "opacity-100": submitState === "success",
                  "opacity-0": submitState !== "success",
                },
              ])}
            />

            {submitState === "error" && (
              <XCircleIcon className="absolute h-6 w-6 text-white transition-opacity" />
            )}
            <span
              className={cc([
                "mx-2 inline-block opacity-0 transition-opacity",
                {
                  "opacity-100": submitState === "ready",
                },
              ])}
            >
              {schoolsPdfFormButton}
            </span>
          </button>
        </div>
      </form>
      <Modal
        isOpen={showModal}
        onRequestClose={closeModal}
        showCloseButton
        closeOnClickAway
      >
        <Content {...modalContent} />
      </Modal>
    </div>
  )
}

export default function DownloadForm() {
  const { schoolsPdfTitle, schoolsPdfSubtitle } = useTranslations()
  return (
    <div className="relative bg-zinc-800 py-16 lg:py-24" id="download">
      <div className="relative mx-auto max-w-7xl grid-cols-8 gap-8 space-y-8 px-4 sm:grid sm:space-y-0 sm:px-6 md:gap-16 lg:px-8">
        <div className="col-span-3 max-w-sm lg:col-span-2">
          <BrochureCover className="rounded-sm rounded-r-lg shadow-2xl" />
        </div>
        <div className="col-span-5 mx-auto flex max-w-lg flex-col lg:col-span-3">
          <div className="grow">
            <h2 className="text-3xl font-extrabold tracking-tight text-zinc-100 sm:text-4xl">
              {schoolsPdfTitle}
            </h2>
            <p className="mt-8 text-lg text-zinc-200 ">{schoolsPdfSubtitle}</p>
          </div>
        </div>
        <div className="col-span-8 mx-auto w-full lg:col-span-3">
          <div className="h-full lg:max-w-xl">
            <Form />
          </div>
        </div>
      </div>
    </div>
  )
}
